import React, { useRef, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Box, Typography } from '@mui/material';
import { generateMqttChartOptions, generateMqttChartData, generateSkeletonChart } from '../../utils/mqttUtils';
import PropTypes from 'prop-types';
import zoomPlugin from 'chartjs-plugin-zoom';
import { sensorTypeLabels } from '../../utils/constants';

// Chart.js time scale and date adapter imports
import {
    Chart as ChartJS,
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    zoomPlugin
);

function OverlayChart({ 
    sensorType,
    actionType,
    sensorHistoricalData,
    actionHistoricalData,
    sensorThresholds,
    selectedTimeRange = 'infinity'
}) {
    const chartRef = useRef(null);
    
    // Debug: Track re-renders (remove this log after debugging)
    // console.log(`OverlayChart re-rendering for ${sensorType} + ${actionType}:`, {
    //     sensorHistoricalDataLength: sensorHistoricalData?.length,
    //     actionHistoricalDataLength: actionHistoricalData?.length,
    //     sensorThresholds,
    //     selectedTimeRange
    // });

    const { chartData, chartOptions } = useMemo(() => {
        const safeSensorData = sensorHistoricalData ?? [];
        const safeActionData = actionHistoricalData ?? [];

        // If no data available for either, show skeleton
        if (safeSensorData.length === 0 && safeActionData.length === 0) {
            const { skeletonData, skeletonOptions } = generateSkeletonChart(`${sensorType} + ${actionType}`);
            return { chartData: skeletonData, chartOptions: skeletonOptions };
        }

        const dynamicOptions = generateMqttChartOptions(
            `${sensorType} Sensor + ${actionType} Status`, 
            sensorType
        );

        // Set up single y-axis for both sensor data and normalized action data
        // Use sensor's predefined min/max range for consistent scaling
        const sensorKey = sensorType.toLowerCase();
        const sensorMin = sensorTypeLabels[sensorKey]?.min || 0;
        const sensorMax = sensorTypeLabels[sensorKey]?.max || 100;
        
        dynamicOptions.scales = {
            ...dynamicOptions.scales,
            y: {
                type: 'linear',
                position: 'left',
                display: true,
                min: sensorMin, // Fixed min from sensor definition
                max: sensorMax, // Fixed max from sensor definition
                title: {
                    display: true,
                    text: `${sensorType} Value (Status Scaled)`,
                    color: 'white'
                },
                ticks: {
                    color: 'white'
                },
                grid: {
                    color: 'rgba(255,255,255,0.1)'
                }
            }
        };

        // Set up threshold lines for sensor data
        const thresholdLines = { datasets: [] };
        const safeSensorThresholds = sensorThresholds || { high: null, low: null };
        
        // Debug: Log threshold setup (can be removed after testing)
        console.log(`OverlayChart thresholds for ${sensorType}:`, safeSensorThresholds);
        console.log(`OverlayChart safeSensorData length: ${safeSensorData.length}, first few points:`, safeSensorData.slice(0, 3));

        if (safeSensorThresholds?.high !== null && safeSensorThresholds?.high !== undefined && typeof safeSensorThresholds?.high === 'number') {
            thresholdLines.datasets.push({
                label: `${sensorType} High Threshold`,
                data: safeSensorData.map(point => ({
                    x: new Date(point.timestamp),
                    y: safeSensorThresholds?.high
                })),
                borderColor: 'rgba(255, 0, 0, 0.8)',
                borderDash: [5, 5],
                pointRadius: 0,
                borderWidth: 2,
                fill: false,
                yAxisID: 'y' // Left axis
            });
        }

        if (safeSensorThresholds?.low !== null && safeSensorThresholds?.low !== undefined && typeof safeSensorThresholds?.low === 'number') {
            thresholdLines.datasets.push({
                label: `${sensorType} Low Threshold`,
                data: safeSensorData.map(point => ({
                    x: new Date(point.timestamp),
                    y: safeSensorThresholds?.low
                })),
                borderColor: 'rgba(0, 0, 255, 0.8)',
                borderDash: [5, 5],
                pointRadius: 0,
                borderWidth: 2,
                fill: false,
                yAxisID: 'y' // Left axis
            });
        }

        // Configure time unit based on selected range
        let timeUnit = 'day';
        let displayFormat = 'MM/dd';

        switch (selectedTimeRange) {
            case '8h':
                timeUnit = 'hour';
                displayFormat = 'h:mm a';
                break;
            case '1d':
                timeUnit = 'hour';
                displayFormat = 'h:mm a';
                break;
            case '1w':
                timeUnit = 'day';
                displayFormat = 'MM/dd';
                break;
            case '2w':
                timeUnit = 'day';
                displayFormat = 'MM/dd';
                break;
            default:
                timeUnit = 'day';
                displayFormat = 'MM/dd';
        }

        // Set up time scale
        dynamicOptions.scales.x = {
            type: 'time',
            display: true,
            time: {
                unit: timeUnit,
                displayFormats: {
                    day: displayFormat
                }
            },
            ticks: {
                color: 'white'
            },
            grid: {
                color: 'rgba(255,255,255,0.1)'
            }
        };

        // Configure zoom functionality
        dynamicOptions.plugins = {
            ...dynamicOptions.plugins,
            zoom: {
                zoom: {
                    wheel: {
                        enabled: window.innerWidth > 1024,
                        speed: 0.1,
                    },
                    pinch: {
                        enabled: window.innerWidth > 1024,
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: window.innerWidth > 1024,
                    mode: 'x',
                },
                limits: {
                    x: {
                        min: 'original',
                        max: 'original'
                    }
                }
            }
        };

        // Generate datasets for both sensor and action data
        const datasets = [];

        // Add sensor data dataset (left y-axis)
        if (safeSensorData.length > 0) {
            const sensorChartData = generateMqttChartData(
                sensorType, 
                safeSensorData, 
                'transparent',
                sensorType === 'Humidity' ? 'lightgreen' : sensorTypeLabels[sensorType.toLowerCase()]?.["color"] || 'lightsalmon',
                sensorType === 'Temperature' ? 'lightsalmon' : sensorTypeLabels[sensorType.toLowerCase()]?.["color"] || 'lightgreen'
            );
            
            // Modify dataset to use left y-axis
            const sensorDataset = {
                ...sensorChartData.datasets[0],
                label: `${sensorType} Sensor`,
                yAxisID: 'y'
            };
            datasets.push(sensorDataset);
        }

        // Add action data dataset (normalized to sensor's defined min/max range)
        if (safeActionData.length > 0) {
            // Get sensor's defined min/max range from constants (not actual data range)
            const sensorKey = sensorType.toLowerCase();
            const sensorMin = sensorTypeLabels[sensorKey]?.min || 0;
            const sensorMax = sensorTypeLabels[sensorKey]?.max || 100;
            const sensorRange = sensorMax - sensorMin;
            
            // Create normalized action data (scale 0-1 to sensorMin-sensorMax)
            const normalizedActionData = safeActionData.map(item => {
                const actionKey = actionType.toLowerCase();
                const originalValue = typeof item[actionKey] === 'number' ? item[actionKey] : 0;
                const normalizedValue = sensorMin + (originalValue * sensorRange); // Scale from 0-1 to sensorMin-sensorMax
                return {
                    ...item,
                    [actionKey]: normalizedValue
                };
            });

            const actionChartData = generateMqttChartData(
                actionType, 
                normalizedActionData, 
                'transparent',
                sensorTypeLabels[actionType.toLowerCase()]?.["color"] || 'lightgray',
                sensorTypeLabels[actionType.toLowerCase()]?.["color"] || 'lightgray'
            );
            
            // Modify dataset to use left y-axis (same as sensor) and gray styling
            const actionDataset = {
                ...actionChartData.datasets[0],
                label: `${actionType} Status (Scaled)`,
                borderColor: sensorTypeLabels[actionType.toLowerCase()]?.["color"] === 'lightgray' 
                    ? 'rgba(169, 169, 169, 0.8)' // lightgray with transparency
                    : 'rgba(192, 192, 192, 0.8)', // fallback gray
                backgroundColor: sensorTypeLabels[actionType.toLowerCase()]?.["color"] === 'lightgray' 
                    ? 'rgba(169, 169, 169, 0.1)'
                    : 'rgba(192, 192, 192, 0.1)',
                borderWidth: 3,
                borderDash: [10, 5], // Dashed line to differentiate
                yAxisID: 'y' // Use same axis as sensor data
            };
            datasets.push(actionDataset);
        }

        // Add threshold lines
        console.log(`OverlayChart adding ${thresholdLines.datasets.length} threshold datasets:`, 
            thresholdLines.datasets.map(d => ({
                label: d.label, 
                dataLength: d.data?.length, 
                firstDataPoint: d.data?.[0],
                lastDataPoint: d.data?.[d.data?.length - 1]
            }))
        );
        datasets.push(...thresholdLines.datasets);

        const combinedChartData = {
            labels: [], // Chart.js will use the time scale from the data
            datasets: datasets
        };
        
        return { chartData: combinedChartData, chartOptions: dynamicOptions };
    }, [sensorType, actionType, sensorHistoricalData, actionHistoricalData, sensorThresholds, selectedTimeRange]);

    return (
        <Box 
            className="mqtt-chart-box overlay-chart"
            sx={{ 
                position: 'relative',
                margin: 'auto'
            }}
        >
            <Typography variant="h6" color="white" textAlign="center">
                {sensorType} Sensor + {actionType} Status (Overlay)
            </Typography>
            {
                chartData && chartOptions ? (
                    <Typography variant="body2" color="gray" textAlign="center" sx={{ mb: 1 }}>
                        Combined view: {sensorType} values + {actionType} status (scaled to sensor range)
                    </Typography>
                ) : (
                    <Typography variant="body1" color="gray" textAlign="center">
                        No data available for overlay chart.
                    </Typography>
                )}
            <Line 
                ref={chartRef}
                data={chartData || {labels: [], datasets: []}} 
                options={{
                    ...(chartOptions || {}),
                    plugins: {
                        ...(chartOptions?.plugins || {}),
                        legend: {
                            display: true,
                            position: 'top',
                            labels: {
                                color: 'white',
                                usePointStyle: true,
                                padding: 20
                            }
                        },
                        zoom: {
                            zoom: {
                                wheel: {
                                    enabled: window.innerWidth > 1024,
                                    speed: 0.1,
                                },
                                pinch: {
                                    enabled: window.innerWidth > 1024,
                                },
                                mode: 'x',
                            },
                            pan: {
                                enabled: window.innerWidth > 1024,
                                mode: 'x',
                            },
                            limits: {
                                x: {
                                    min: 'original',
                                    max: 'original'
                                },
                                y: {
                                    min: 'original',
                                    max: 'original'
                                }
                            }
                        }
                    },
                    animation: {
                        duration: 300 // Smooth transition when toggling
                    },
                    elements: {
                        point: {
                            radius: sensorHistoricalData?.length > 1000 || actionHistoricalData?.length > 1000 ? 0 : (window.innerWidth > 1024 ? 3 : 1)
                        }
                    },
                    interaction: {
                        mode: window.innerWidth > 1024 ? 'nearest' : 'index',
                        intersect: false,
                    },
                    responsive: true,
                    maintainAspectRatio: false
                }} 
            />
        </Box>
    );
}

OverlayChart.propTypes = {
    sensorType: PropTypes.string.isRequired,
    actionType: PropTypes.string.isRequired,
    sensorHistoricalData: PropTypes.arrayOf(PropTypes.object),
    actionHistoricalData: PropTypes.arrayOf(PropTypes.object),
    sensorThresholds: PropTypes.shape({
        high: PropTypes.number,
        low: PropTypes.number
    }),
    selectedTimeRange: PropTypes.string
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(OverlayChart, (prevProps, nextProps) => {
    return (
        prevProps.sensorType === nextProps.sensorType &&
        prevProps.actionType === nextProps.actionType &&
        prevProps.selectedTimeRange === nextProps.selectedTimeRange &&
        prevProps.sensorThresholds?.high === nextProps.sensorThresholds?.high &&
        prevProps.sensorThresholds?.low === nextProps.sensorThresholds?.low &&
        JSON.stringify(prevProps.sensorHistoricalData) === JSON.stringify(nextProps.sensorHistoricalData) &&
        JSON.stringify(prevProps.actionHistoricalData) === JSON.stringify(nextProps.actionHistoricalData)
    );
});
